import { type ReactNode } from 'react';
import { zIndex, vars } from '@/theme';
import { testIds } from '@/test/testIds';
import { SPINNER_SIZE, useLoadingZoneRegistration } from '../LoadingZone';
import { Spinner } from '@/components/primitives';

/**
 * The veil every region overlay shares (LoadingOverlay's own look, reused by
 * LoadingState `shape="known"` so there is one overlay style, #1561 W2):
 * translucent white over exactly the region, blocks the pointer, wait cursor.
 */
export const regionVeilStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: zIndex.elevated,
  pointerEvents: 'auto',
  cursor: 'wait',
} as const;

export interface LoadingOverlayProps {
  /** Whether the overlay is active */
  active: boolean;
  /** Content to overlay — always visible */
  children: ReactNode;
}

/**
 * LoadingOverlay — Semi-transparent overlay for refetch/filter change states.
 *
 * Auto-registers as a loading zone while `active` is true. The veil always
 * covers this region; when another zone owns the screen's one spinner
 * (#1561 W5) this one draws the veil without a spinner.
 *
 * @example
 * <LoadingOverlay active={isFetching && !isLoading}>
 *   <CardGrid>{items.map(renderItem)}</CardGrid>
 * </LoadingOverlay>
 */
export function LoadingOverlay({ active, children }: LoadingOverlayProps) {
  const { hideSpinner } = useLoadingZoneRegistration(active);

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {children}

      {active && (
        <div
          data-testid={testIds.ui.loadingOverlay}
          style={{
            ...regionVeilStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!hideSpinner && <Spinner size={SPINNER_SIZE} color={vars.color.success.base} />}
        </div>
      )}
    </div>
  );
}
